/**
 * LOCKER-534 — deterministic fake temperature readings for the demo.
 *
 * Pure port of the mockup's `seed() / val() / level() / series()`
 * (Main.dc.html). No probe exists: every value is computed from the door id,
 * the scenario and an injected `now`. Same inputs → same curve on every
 * reload and device.
 *
 * One deliberate change vs the mockup: its baseline wobble was phased on
 * "minutes ago", so with a live clock the whole history slid every minute.
 * Here the wobble is phased on the ABSOLUTE minute of each sample, so a past
 * reading stays put as time moves. The scenario overlays stay relative to
 * `now` — "critical" always means "above 10 °C for about 2 h".
 */
import type { TemperatureDemoScenario } from "./temperatureDemoTypes"

export type TemperatureLevel = "ok" | "warn" | "crit"
export type TemperatureRange = "6h" | "24h" | "7d"

/** Cold-chain thresholds (°C). */
export const TARGET_MIN = 2
export const TARGET_MAX = 8
export const CRITICAL_MIN = 0
export const CRITICAL_MAX = 10

/** [span minutes, step minutes] per range — same sampling as the mockup. */
export const RANGE_SAMPLING: Record<TemperatureRange, [number, number]> = {
  "6h": [360, 5],
  "24h": [1440, 15],
  "7d": [10080, 30],
}

const MINUTE_MS = 60_000

/** 0 ≤ seed < ~10.3, stable per door id. */
export const doorSeed = (doorId: string): number => {
  let h = 7
  for (let i = 0; i < doorId.length; i++) {
    h = (h * 31 + doorId.charCodeAt(i)) % 997
  }
  return h / 97
}

/** `now` floored to the minute, so every render inside one minute agrees. */
const nowMinute = (now: Date): number => Math.floor(now.getTime() / MINUTE_MS)

/** Local minute of the day (0..1439) of a sample `minutesAgo` before `now`. */
const minuteOfDay = (now: Date, minutesAgo: number): number => {
  const d = new Date((nowMinute(now) - minutesAgo) * MINUTE_MS)
  return d.getHours() * 60 + d.getMinutes()
}

/** Simulated °C of `doorId` under `scenario`, `minutesAgo` before `now`. */
export const temperatureAt = (
  doorId: string,
  scenario: TemperatureDemoScenario,
  minutesAgo: number,
  now: Date,
): number => {
  const s = doorSeed(doorId)
  const t = nowMinute(now) - minutesAgo
  let v = 4.6 + 0.5 * Math.sin(t / 37 + s) + 0.25 * Math.sin(t / 11 + 2 * s)
  // Two door-opening bumps a day, at 09:30 and 16:10 local.
  const clock = minuteOfDay(now, minutesAgo)
  v +=
    1.4 * Math.exp(-Math.pow((clock - 570) / 12, 2)) +
    1.1 * Math.exp(-Math.pow((clock - 970) / 12, 2))
  const m = minutesAgo
  if (scenario === "warning") v += 4.2 * Math.max(0, 1 - m / 90)
  if (scenario === "critical") v += 7.2 * Math.max(0, 1 - m / 150)
  if (scenario === "past") v += 6.6 * Math.exp(-Math.pow((m - 560) / 40, 2))
  return v
}

export const temperatureLevel = (v: number): TemperatureLevel =>
  v > CRITICAL_MAX || v < CRITICAL_MIN
    ? "crit"
    : v > TARGET_MAX || v < TARGET_MIN
      ? "warn"
      : "ok"

/** Any sample out of 2–8 °C in the last 24 h (15-min sampling, as the mockup). */
export const hadBreachInLast24h = (
  doorId: string,
  scenario: TemperatureDemoScenario,
  now: Date,
): boolean => {
  for (let m = 0; m <= 1440; m += 15) {
    if (temperatureLevel(temperatureAt(doorId, scenario, m, now)) !== "ok") {
      return true
    }
  }
  return false
}

export type TemperaturePoint = {
  minutesAgo: number
  value: number
  level: TemperatureLevel
}

/** One contiguous run of out-of-range samples. */
export type TemperatureBreach = {
  critical: boolean
  /** Start / end of the run, in minutes before `now`. */
  startMinutesAgo: number
  endMinutesAgo: number
  /** The run reaches the newest sample. */
  ongoing: boolean
  /** Above 8 °C (`max`) or below 2 °C (`min`). */
  high: boolean
  peak: number
  durationMinutes: number
  /** Shaded chart span, half a step wide on each side, clamped to the range. */
  spanFromMinutesAgo: number
  spanToMinutesAgo: number
}

export type TemperatureSeries = {
  range: TemperatureRange
  spanMinutes: number
  stepMinutes: number
  /** Oldest first; the last point is `now`. */
  points: TemperaturePoint[]
  breaches: TemperatureBreach[]
  current: number
  currentLevel: TemperatureLevel
  min: number
  max: number
  outOfRangeMinutes: number
}

export const temperatureSeries = (
  doorId: string,
  scenario: TemperatureDemoScenario,
  range: TemperatureRange,
  now: Date,
): TemperatureSeries => {
  const [span, step] = RANGE_SAMPLING[range]
  const n = span / step
  const points: TemperaturePoint[] = []
  for (let i = 0; i <= n; i++) {
    const minutesAgo = span - i * step
    const value = temperatureAt(doorId, scenario, minutesAgo, now)
    points.push({ minutesAgo, value, level: temperatureLevel(value) })
  }

  type Run = { a: number; b: number; max: number; min: number; crit: boolean }
  const runs: Run[] = []
  let cur: Run | null = null
  for (let i = 0; i < points.length; i++) {
    const p = points[i]
    if (p.level !== "ok") {
      if (!cur) cur = { a: i, b: i, max: p.value, min: p.value, crit: false }
      cur.b = i
      cur.max = Math.max(cur.max, p.value)
      cur.min = Math.min(cur.min, p.value)
      if (p.level === "crit") cur.crit = true
    } else if (cur) {
      runs.push(cur)
      cur = null
    }
  }
  if (cur) runs.push(cur)

  const half = step / 2
  const breaches = runs.map((r): TemperatureBreach => {
    const high = r.max > TARGET_MAX
    return {
      critical: r.crit,
      startMinutesAgo: points[r.a].minutesAgo,
      endMinutesAgo: points[r.b].minutesAgo,
      ongoing: r.b === n,
      high,
      peak: high ? r.max : r.min,
      durationMinutes: (r.b - r.a + 1) * step,
      spanFromMinutesAgo: Math.min(span, points[r.a].minutesAgo + half),
      spanToMinutesAgo: Math.max(0, points[r.b].minutesAgo - half),
    }
  })

  const values = points.map((p) => p.value)
  const last = points[n]
  return {
    range,
    spanMinutes: span,
    stepMinutes: step,
    points,
    breaches,
    current: last.value,
    currentLevel: last.level,
    min: Math.min(...values),
    max: Math.max(...values),
    outOfRangeMinutes: breaches.reduce((a, b) => a + b.durationMinutes, 0),
  }
}

/** The newest `limit` breaches, newest first (the drawer's event list). */
export const latestBreaches = (
  series: TemperatureSeries,
  limit = 3,
): TemperatureBreach[] => series.breaches.slice().reverse().slice(0, limit)
